import React, { useEffect, useState } from 'react';
import { FaBolt, FaClock, FaCoins, FaStar, FaRocket, FaChartLine } from 'react-icons/fa';
import { MdOutlineSpeed, MdOutlineLocalOffer, MdTimer } from 'react-icons/md';
import { IoFlash, IoTrendingUp, IoStatsChart } from 'react-icons/io5';

const HotProductSection = ({ products, onInvest }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  if (!products || products.length === 0) return null;

  // Helper to get icon based on company name
  const getCompanyIcon = (name) => {
    name = name.toLowerCase();
    if (name.includes('tech') || name.includes('digital')) return <MdOutlineSpeed />;
    if (name.includes('bank') || name.includes('finance')) return <FaCoins />;
    if (name.includes('energy') || name.includes('oil')) return <FaBolt />;
    if (name.includes('real estate') || name.includes('property')) return <IoStatsChart />;
    if (name.includes('retail') || name.includes('consumer')) return <FaChartLine />;
    if (name.includes('pharma') || name.includes('health')) return <FaStar />;
    return <FaRocket />;
  };

  // Helper to get color based on company sector
  const getCompanyColor = (name) => {
    name = name.toLowerCase();
    if (name.includes('tech') || name.includes('digital')) return '#2563EB';
    if (name.includes('bank') || name.includes('finance')) return '#059669';
    if (name.includes('energy') || name.includes('oil')) return '#DC2626';
    if (name.includes('real estate') || name.includes('property')) return '#7C3AED';
    if (name.includes('retail') || name.includes('consumer')) return '#EA580C';
    if (name.includes('pharma') || name.includes('health')) return '#0891B2';
    return '#e74c3c';
  };

  return (
    <div className={`hp-featured-section ${isVisible ? 'hp-visible' : ''}`}>
      <div className="hp-section-header">
        <div className="hp-header-content">
          <h2 className="hp-section-title">🔥 Featured Investments</h2>
          <p className="hp-section-subtitle">Premium opportunities • Limited time</p>
        </div>
        <div className="hp-trending-badge">
          <IoTrendingUp className="hp-trending-icon" />
          <span>Trending</span>
        </div>
      </div>
      
      <div className="hp-products-grid">
        {products.map((product, index) => {
          const companyColor = getCompanyColor(product.name);
          const roiPercentage = Math.round((product.totalIncome / product.price - 1) * 100);
          
          return (
            <div key={index} className="hp-featured-card">
              <div className="hp-featured-badge">
                <FaStar className="hp-badge-icon" />
                <span>Hot</span>
              </div>
              
              <div className="hp-card-header">
                <div className="hp-company-image">
                  <img 
                    src={`https://api.globallequityfinance.com${product.imageUrl}`}
                    alt={product.name}
                    className="hp-company-logo"
                  />
                </div>
                <div className="hp-company-info">
                  <h3 className="hp-company-name">{product.name}</h3>
                  <div className="hp-company-icon" style={{ color: companyColor }}>
                    {getCompanyIcon(product.name)}
                  </div>
                </div>
              </div>
              
              <div className="hp-metrics-section">
                <div className="hp-metric-row">
                  <div className="hp-metric">
                    <span className="hp-metric-label">Duration</span>
                    <span className="hp-metric-value">{product.cycle}d</span>
                  </div>
                  <div className="hp-metric">
                    <span className="hp-metric-label">Daily</span>
                    <span className="hp-metric-value">KES {product.dailyIncome}</span>
                  </div>
                  <div className="hp-metric">
                    <span className="hp-metric-label">Total</span>
                    <span className="hp-metric-value hp-roi-highlight">KES {product.totalIncome}</span>
                  </div>
                </div>
                
                <div className="hp-investment-info">
                  <div className="hp-price-section">
                    <span className="hp-price-label">Investment</span>
                    <span className="hp-price-value">KES {product.price.toLocaleString()}</span>
                  </div>
                  <div className="hp-availability">
                    <span className="hp-available-count">{product.available}</span>
                    <span className="hp-available-label">left</span>
                  </div>
                </div>
              </div>
              
              <div className="hp-card-footer">
                <button
                  onClick={() => onInvest(product)}
                  className={`hp-invest-button ${product.available < 1 ? 'hp-disabled' : ''}`}
                  disabled={product.available < 1}
                  style={{ backgroundColor: product.available >= 1 ? companyColor : '#64748b' }}
                >
                  <span className="hp-button-text">
                    {product.available < 1 ? 'Sold Out' : 'Invest Now'}
                  </span>
                  {product.available >= 1 && <IoFlash className="hp-button-icon" />}
                </button>
              </div>
            </div>
          );
        })}
      </div>
      
      <style jsx>{`
        .hp-featured-section {
          --primary: #6366f1;
          --primary-light: #818cf8;
          --primary-dark: #4f46e5;
          --accent: #10b981;
          --accent-dark: #059669;
          --featured-gold: #f59e0b;
          --featured-gold-light: #fbbf24;
          --hot-orange: #ff6b35;
          --hot-red: #e74c3c;
          --dark-bg: #0f172a;
          --card-bg: #1e293b;
          --border-color: rgba(255, 107, 53, 0.3);
          --text-primary: #ffffff;
          --text-secondary: #cbd5e1;
          --text-muted: #64748b;
          --animation-speed: 0.3s;
          
          margin: 16px;
          opacity: 0;
          transform: translateY(20px);
          transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .hp-featured-section.hp-visible {
          opacity: 1;
          transform: translateY(0);
        }
        
        .hp-section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
          padding: 0 4px;
        }
        
        .hp-header-content {
          flex: 1;
        }
        
        .hp-section-title {
          font-size: 18px;
          font-weight: 700;
          margin: 0 0 4px;
          background: linear-gradient(135deg, var(--hot-orange), var(--featured-gold));
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          color: var(--hot-orange);
        }
        
        .hp-section-subtitle {
          font-size: 12px;
          color: var(--text-secondary);
          margin: 0;
          opacity: 0.8;
        }
        
        .hp-trending-badge {
          background: linear-gradient(135deg, var(--hot-orange), var(--hot-red));
          color: white;
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 11px;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 4px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          box-shadow: 0 2px 8px rgba(255, 107, 53, 0.3);
          animation: pulse-glow 2s infinite;
        }
        
        @keyframes pulse-glow {
          0%, 100% {
            box-shadow: 0 2px 8px rgba(255, 107, 53, 0.3);
          }
          50% {
            box-shadow: 0 4px 16px rgba(255, 107, 53, 0.5);
          }
        }
        
        .hp-trending-icon {
          font-size: 12px;
        }
        
        .hp-products-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 14px;
          padding: 0 4px;
        }
        
        .hp-featured-card {
          background: var(--card-bg);
          border-radius: 16px;
          overflow: hidden;
          position: relative;
          border: 2px solid var(--border-color);
          transition: all var(--animation-speed) ease;
          box-shadow: 0 4px 20px rgba(255, 107, 53, 0.1);
        }
        
        .hp-featured-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 30px rgba(255, 107, 53, 0.2);
          border-color: var(--hot-orange);
        }
        
        .hp-featured-badge {
          position: absolute;
          top: 10px;
          right: 10px;
          background: linear-gradient(135deg, var(--featured-gold), var(--featured-gold-light));
          color: white;
          padding: 4px 8px;
          border-radius: 8px;
          font-size: 9px;
          font-weight: 700;
          display: flex;
          align-items: center;
          gap: 3px;
          z-index: 2;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        
        .hp-badge-icon {
          font-size: 8px;
        }
        
        .hp-card-header {
          padding: 14px 16px 12px;
          display: flex;
          align-items: center;
          gap: 12px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.05);
        }
        
        .hp-company-image {
          width: 44px;
          height: 44px;
          border-radius: 12px;
          overflow: hidden;
          flex-shrink: 0;
          background: rgba(255, 255, 255, 0.05);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }
        
        .hp-company-logo {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform var(--animation-speed) ease;
        }
        
        .hp-featured-card:hover .hp-company-logo {
          transform: scale(1.1);
        }
        
        .hp-company-info {
          flex: 1;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        
        .hp-company-name {
          font-size: 15px;
          font-weight: 600;
          margin: 0;
          color: var(--text-primary);
          line-height: 1.3;
        }
        
        .hp-company-icon {
          font-size: 18px;
          opacity: 0.8;
        }
        
        .hp-metrics-section {
          padding: 12px 16px;
        }
        
        .hp-metric-row {
          display: flex;
          gap: 10px;
          margin-bottom: 12px;
        }
        
        .hp-metric {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 2px;
          background: rgba(255, 255, 255, 0.02);
          padding: 8px 10px;
          border-radius: 8px;
          border: 1px solid rgba(255, 255, 255, 0.05);
          text-align: center;
        }
        
        .hp-metric-label {
          font-size: 10px;
          color: var(--text-secondary);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          font-weight: 500;
        }
        
        .hp-metric-value {
          font-size: 12px;
          font-weight: 700;
          color: var(--text-primary);
          line-height: 1.2;
        }
        
        .hp-roi-highlight {
          color: var(--accent);
        }
        
        .hp-investment-info {
          display: flex;
          justify-content: space-between;
          align-items: center;
          background: rgba(255, 107, 53, 0.05);
          border-radius: 10px;
          padding: 10px 12px;
          border: 1px solid rgba(255, 107, 53, 0.1);
        }
        
        .hp-price-section {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        
        .hp-price-label {
          font-size: 10px;
          color: var(--text-secondary);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          font-weight: 500;
        }
        
        .hp-price-value {
          font-size: 14px;
          font-weight: 700;
          color: var(--hot-orange);
        }
        
        .hp-availability {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 2px;
        }
        
        .hp-available-count {
          font-size: 16px;
          font-weight: 700;
          color: var(--text-primary);
        }
        
        .hp-available-label {
          font-size: 9px;
          color: var(--text-secondary);
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        
        .hp-card-footer {
          padding: 12px 16px 16px;
        }
        
        .hp-invest-button {
          width: 100%;
          color: white;
          border: none;
          border-radius: 10px;
          padding: 12px 16px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          transition: all var(--animation-speed) ease;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          box-shadow: 0 3px 10px rgba(0, 0, 0, 0.2);
        }
        
        .hp-invest-button:hover:not(.hp-disabled) {
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
        }
        
        .hp-invest-button.hp-disabled {
          background: var(--text-muted) !important;
          cursor: not-allowed;
          opacity: 0.6;
        }
        
        .hp-button-text {
          font-weight: 600;
        }
        
        .hp-button-icon {
          font-size: 14px;
        }
        
        /* Media Queries */
        @media (max-width: 768px) {
          .hp-products-grid {
            grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
            gap: 12px;
          }
          
          .hp-section-title {
            font-size: 16px;
          }
          
          .hp-company-image {
            width: 40px;
            height: 40px;
          }
          
          .hp-company-name {
            font-size: 14px;
          }
          
          .hp-company-icon {
            font-size: 16px;
          }
          
          .hp-card-header {
            padding: 12px 14px 10px;
            gap: 10px;
          }
          
          .hp-metrics-section {
            padding: 10px 14px;
          }
          
          .hp-card-footer {
            padding: 10px 14px 14px;
          }
          
          .hp-metric {
            padding: 6px 8px;
          }
          
          .hp-metric-value {
            font-size: 11px;
          }
          
          .hp-invest-button {
            padding: 10px 14px;
            font-size: 12px;
          }
          
          .hp-investment-info {
            padding: 8px 10px;
          }
          
          .hp-price-value {
            font-size: 13px;
          }
          
          .hp-available-count {
            font-size: 14px;
          }
        }
        
        @media (max-width: 480px) {
          .hp-products-grid {
            grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
            gap: 10px;
          }
          
          .hp-section-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
            margin-bottom: 14px;
          }
          
          .hp-trending-badge {
            align-self: flex-end;
            padding: 4px 10px;
            font-size: 10px;
          }
          
          .hp-metric-row {
            gap: 8px;
          }
          
          .hp-metric {
            padding: 6px 6px;
          }
          
          .hp-metric-value {
            font-size: 10px;
          }
          
          .hp-metric-label {
            font-size: 9px;
          }
          
          .hp-company-name {
            font-size: 13px;
          }
          
          .hp-invest-button {
            font-size: 11px;
            padding: 10px 12px;
          }
          
          .hp-price-value {
            font-size: 12px;
          }
          
          .hp-available-count {
            font-size: 13px;
          }
        }
        
        @media (max-width: 360px) {
          .hp-products-grid {
            grid-template-columns: 1fr;
            gap: 10px;
          }
          
          .hp-metric-row {
            gap: 6px;
          }
          
          .hp-metric {
            padding: 5px 4px;
          }
          
          .hp-investment-info {
            padding: 6px 8px;
          }
          
          .hp-card-header {
            padding: 10px 12px 8px;
          }
          
          .hp-metrics-section {
            padding: 8px 12px;
          }
          
          .hp-card-footer {
            padding: 8px 12px 12px;
          }
        }
      `}</style>
    </div>
  );
};

export default HotProductSection;